import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { ApiService } from "./api/api.service";
import {
  ChatContact,
  ChatContactsParams,
  ChatMessage,
  ChatMessageCreatePayload,
  ChatMessagesParams,
  ChatCreatePayload,
  ChatItem,
  ChatParams,
} from "src/app/modules/chat/models";
import { PaginationData } from "../interfaces/orgadmin/course-organization";
import { Guid } from "../types/guid";

interface ChatUnreadSummary {
  has_unread: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ChatService {
  private readonly incomingMessageSubject = new Subject<ChatMessage>();
  private readonly unreadSummarySubject = new BehaviorSubject<boolean>(false);
  readonly incomingMessage$ = this.incomingMessageSubject.asObservable();
  readonly hasUnread$ = this.unreadSummarySubject.asObservable();

  constructor(private readonly apiService: ApiService) {}

  handleIncomingMessage(message: ChatMessage): void {
    this.incomingMessageSubject.next(message);

    if (!message.is_own) {
      this.unreadSummarySubject.next(true);
    }
  }

  refreshUnreadSummary(): void {
    this.getUnreadSummary().subscribe({
      next: (summary) => this.unreadSummarySubject.next(summary.has_unread),
    });
  }

  getChats(params: ChatParams): Observable<PaginationData<ChatItem>> {
    return this.apiService.getRequest<PaginationData<ChatItem>>(
      "chats/threads/",
      {
        params: {
          ...(params.search ? { search: params.search } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.offset ? { offset: params.offset } : {}),
        },
      },
    );
  }

  getContacts(
    params: ChatContactsParams,
  ): Observable<PaginationData<ChatContact>> {
    return this.apiService.getRequest<PaginationData<ChatContact>>(
      "chats/contacts/",
      {
        params: {
          ...(params.search ? { search: params.search } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
        },
      },
    );
  }

  createChat(payload: ChatCreatePayload): Observable<ChatItem> {
    return this.apiService.postRequest<ChatItem>("chats/threads/", payload);
  }

  getMessages(
    chatId: Guid,
    params: ChatMessagesParams,
  ): Observable<PaginationData<ChatMessage>> {
    return this.apiService.getRequest<PaginationData<ChatMessage>>(
      `chats/threads/${chatId}/messages/`,
      {
        params: {
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.before ? { before: params.before } : {}),
        },
      },
    );
  }

  sendMessage(
    chatId: Guid,
    payload: ChatMessageCreatePayload,
  ): Observable<ChatMessage> {
    return this.apiService.postRequest<ChatMessage>(
      `chats/threads/${chatId}/messages/`,
      payload,
    );
  }

  readMessages(chatId: Guid): Observable<ChatItem> {
    return this.apiService.postRequest<ChatItem>(
      `chats/threads/${chatId}/read/`,
      {},
    );
  }

  private getUnreadSummary(): Observable<ChatUnreadSummary> {
    return this.apiService.getRequest<ChatUnreadSummary>(
      "chats/unread-summary/",
    );
  }
}
